import React from "react";
import Switch from "@mui/material/Switch";
import BdrSelectPopup from "../../../../../components/BdrInput/BdrSelectPopup";
import EventDateTimeInput from "../../EventDateTimeInput/EventDateTimeInput";
import NumberStepper from "../../NumberStepper/NumberStepper";
import {
  FREQ_WEEKLY,
  FREQ_MONTHLY,
  describeRepeatFromDate,
} from "../../../../../utils/calendarPersonalEvents";

/**
 * The Repeat block for a personal ("SIX") event — shared by CreateEventModal
 * and EditEventModal so the two cannot drift.
 * Plan: BDR_Mobile plans/impl_recurring_personal_events_2026_09_19.md §6.7.
 *
 * Two deliberate absences:
 *
 *  - There is NO weekday or day-of-month picker. The pattern day is derived
 *    from the event's own date, because "starts Monday, repeats Tuesdays" has
 *    no sensible first occurrence. The derived line below the controls is how
 *    the user sees what they picked.
 *  - There is no "ends after N times". Only a date, which the server stores as
 *    an inclusive last day.
 *
 * Available whether or not the event is all-day — the two switches are
 * independent, and all four combinations are valid.
 */
/**
 * `storedMonthDay` (2026-09-25, audit G8): the series' persisted `byMonthDay`,
 * passed by EditEventModal ONLY while the date is still the tapped
 * occurrence's. A snapped occurrence (a "31st" series opened on Feb 28) used
 * to preview "Monthly on the 28th" while the save correctly kept 31.
 */
export default function RepeatFields({ value, onChange, dateMs, disabled, storedMonthDay = null }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const every = parseInt(value.interval, 10) || 1;

  return (
    <>
      <div className="create-event-field">
        <label className="create-event-field-label">Repeats</label>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Switch
            checked={!!value.on}
            disabled={disabled}
            onChange={(e) => set({ on: e.target.checked })}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: "#00e676" },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#00e676",
              },
            }}
          />
          <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem" }}>
            {value.on ? "Repeats on a schedule" : "Does not repeat"}
          </span>
        </div>
      </div>

      {value.on && (
        <>
          <div className="create-event-field">
            <div
              className="create-event-row"
              style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
            >
              <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem" }}>
                Every
              </span>
              {/* NumberStepper, not a bare number input: this is the same
                  kind of control as Refinishers Min/Max in the same modal,
                  and a browser's native spinner arrows look nothing like
                  them. Stored as a string, like the rest of the form. */}
              <NumberStepper
                value={value.interval}
                onChange={(val) => set({ interval: String(val) })}
                min={1}
                max={52}
                disabled={disabled}
                ariaLabel="Repeat interval"
              />
              <BdrSelectPopup
                id="repeat-freq"
                value={value.freq}
                disabled={disabled}
                onChange={(e) => set({ freq: e.target.value })}
                className="bdr-input bdr-input--sm bdr-input--auto bdr-select-trigger"
                /* sits inline in a sentence, so the trigger is only as wide as
                   "weeks" — the list has to size to itself, not to it */
                fitContent
                options={[
                  { value: FREQ_WEEKLY, label: every === 1 ? "week" : "weeks" },
                  {
                    value: FREQ_MONTHLY,
                    label: every === 1 ? "month" : "months",
                  },
                ]}
              />
            </div>
            {/* Derived, never entered: this is the whole pattern in words. */}
            <div
              style={{
                color: "rgba(255,255,255,0.55)",
                fontSize: "0.8rem",
                marginTop: 4,
              }}
            >
              {describeRepeatFromDate(value.freq, value.interval, dateMs, storedMonthDay) ||
                "Pick a date to set the repeat day"}
            </div>
          </div>

          <div className="create-event-field">
            <label className="create-event-field-label">Ends</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Switch
                checked={!!value.endsOn}
                disabled={disabled}
                onChange={(e) =>
                  // Turning this off sends "" on save, which is the sentinel
                  // that CLEARS the end date — null would read as "unchanged".
                  set({ endsOn: e.target.checked ? dateMs || "" : "" })
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#00e676" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#00e676",
                  },
                }}
              />
              {value.endsOn ? (
                <EventDateTimeInput
                  value={value.endsOn}
                  onChange={(ms) => {
                    if (typeof ms === "string") set({ endsOn: ms });
                  }}
                  label="On:"
                  hideTime
                  defaultTime={{ hour24: 12, minute: 0 }}
                />
              ) : (
                <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem" }}>
                  Never
                </span>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
